import React from "react";
import { Link } from "react-router-dom";
import { SectionHeader } from "@/components/shared/Common";
import { Button } from "@/components/ui/button";
import {
  Users2, UserPlus, Building2, ShieldCheck, Radio, HeartPulse,
  Car, Briefcase, ArrowRight, CalendarClock, MapPin, Award,
  ClipboardCheck, ChevronRight, Star, Lock, Zap,
} from "lucide-react";
import ShareOnWhatsApp from "@/components/shared/ShareOnWhatsApp";

const WA_MESSAGE_CLUSTERS = `🛡️ SecuSource — a security workforce platform built by security & CPOs, for the industry.

Looking to *join* or *lead* a cluster team? Verified guards, CPOs, drivers, supervisors — all in one place.

✅ Free to register for professionals
✅ You set your own rate
✅ Book/join full teams (kit, medical, comms already verified)
✅ Ireland & UK
✅ Companies pay a low, transparent fee — pros keep 100%

Register here: https://secure-staff-hub.preview.emergentagent.com/register/staff

Or browse ready-made teams:
https://secure-staff-hub.preview.emergentagent.com/teams`;

const USE_CASES = [
  {
    icon: Zap,
    title: "Event & festival deployments",
    body: "A single search books a 20-person team with a supervisor, first-aider, medical support and radio comms — instead of chasing 20 individual pros in a WhatsApp group.",
  },
  {
    icon: Briefcase,
    title: "Corporate close-protection teams",
    body: "Team lead + 3 CPOs + secure driver, all with matching kit, formal dress and vetted qualifications — pre-arranged and ready to deploy together.",
  },
  {
    icon: Radio,
    title: "Multi-site or mobile response",
    body: "One cluster covering static + patrol + supervisor. Companies book the whole unit; the team lead handles internal scheduling.",
  },
];

const TEAM_ROLES = [
  { icon: UserPlus, badge: "Lead Pro", title: "You lead a team", body: "Create the cluster, invite verified pros by SecuSource ID, edit the profile, submit combined availability, and get booked as a whole unit." },
  { icon: Users2, badge: "Member", title: "You join a team", body: "Accept a lead pro's invite. Get seen alongside verified colleagues. Endorsements flow to both your individual profile and the team's ranking." },
  { icon: Building2, badge: "Company", title: "You book a team", body: "Search by role or cluster. Book a full operational team in one action — kit, medical, comms and licences already verified." },
];

const TEAM_BENEFITS = [
  { icon: Award, title: "Higher ranking", body: "Endorsements at the team level compound onto every member's individual reputation." },
  { icon: ShieldCheck, title: "Verified as a unit", body: "Combined qualifications, kit list, vehicles, medical capability and comms are all visible before a company books." },
  { icon: Lock, title: "Anonymous until confirmed", body: "The team is discoverable by capability — real names, phones and emails only unlock after booking approval." },
  { icon: HeartPulse, title: "Medical & comms baked in", body: "First-aid certified members and radio equipment are exposed on the team card — no more chasing kit inventories." },
  { icon: Car, title: "Vehicles included", body: "Team profiles show combined vehicle access, driving licences and dashcams — critical for CP and mobile response." },
  { icon: ClipboardCheck, title: "One SOP acceptance", body: "The lead pro accepts SOPs once for the team — every member confirms before deployment." },
];

const LEAD_STEPS = [
  { n: 1, title: "Register & get approved", body: "Sign up as a security professional. Once your PSA / SIA licence is verified by admin, you can create a team." },
  { n: 2, title: "Create your cluster", body: "Choose a team name, describe your capability, and set your combined availability." },
  { n: 3, title: "Invite by SecuSource ID", body: "Send invites to verified colleagues using their SS-#### ID. They accept from their own dashboard." },
  { n: 4, title: "Get booked as a unit", body: "Companies see your team on the search page and can request a booking for the whole cluster with one tap." },
];

const COMPANY_STEPS = [
  { n: 1, title: "Register your company", body: "Sign up with your business details. Admin approves within one working day (usually much faster)." },
  { n: 2, title: "Search cluster teams", body: "Filter by county, capability, country (IE / UK), and licence type. Every team card shows kit, medical and vehicle capability upfront." },
  { n: 3, title: "Request booking", body: "One transparent fee — €1/hr per worker OR 3% of the booking, whichever is lower. See the number before you confirm." },
  { n: 4, title: "Deploy & endorse", body: "SOPs, agreement and contact details unlock after confirmation. Leave a team endorsement after the deployment ends." },
];

export default function ClusterTeamsPage() {
  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16 space-y-16">
      {/* Hero */}
      <div className="grid lg:grid-cols-5 gap-8 items-start">
        <div className="lg:col-span-3">
          <div className="inline-flex items-center gap-2 bg-brand-light text-brand border border-brand/20 px-3 py-1 rounded-full text-xs font-outfit font-bold uppercase tracking-widest mb-4">
            <Users2 className="w-3.5 h-3.5" /> Ready-made operational teams
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-outfit font-bold text-white tracking-tight leading-[1.05]">
            Book a whole team.<br />Not a spreadsheet of individuals.
          </h1>
          <p className="text-white/70 mt-5 font-manrope text-base sm:text-lg max-w-2xl">
            Cluster Teams let verified professionals operate as pre-arranged units for events, close protection, mobile response and multi-day operations.
            One lead pro, one search, one booking — combined kit, medical support and radios already confirmed.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Button asChild size="lg" className="bg-brand hover:bg-brand-hover text-white px-6 py-6 font-manrope font-semibold" data-testid="cluster-hero-lead-cta">
              <Link to="/register/staff">Lead a team — join free <ArrowRight className="w-4 h-4 ml-2" /></Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-brand text-brand hover:bg-brand-light px-6 py-6 font-manrope font-semibold" data-testid="cluster-hero-book-cta">
              <Link to="/register/company">Book a team as a company</Link>
            </Button>
            <ShareOnWhatsApp message={WA_MESSAGE_CLUSTERS} size="lg" testId="cluster-hero-share" />
          </div>
        </div>

        {/* Example team card */}
        <div className="lg:col-span-2">
          <div className="card-flat p-5 space-y-3 border-l-4 border-l-brand" data-testid="cluster-example-card">
            <div className="flex items-center justify-between">
              <span className="ss-id">TEAM-0007</span>
              <span className="verified-chip"><ShieldCheck className="w-3.5 h-3.5" /> Verified</span>
            </div>
            <div className="font-outfit font-bold text-white text-lg">Northside Ops Cluster</div>
            <div className="text-sm text-white/70 font-manrope flex items-center gap-2"><MapPin className="w-3.5 h-3.5" /> Dublin · Belfast · IE / UK</div>
            <div className="flex flex-wrap gap-1">
              <span className="chip">CPO / VPO ×3</span>
              <span className="chip">Team Lead ×1</span>
              <span className="chip">Security Driver ×1</span>
              <span className="chip">First Aid ×3</span>
            </div>
            <div className="grid grid-cols-3 gap-2 text-center text-xs font-outfit">
              <div className="bg-[#0E0E0E] rounded p-2"><div className="text-white/60 uppercase tracking-widest">Combined exp</div><div className="text-white font-bold text-base">42 yrs</div></div>
              <div className="bg-[#0E0E0E] rounded p-2"><div className="text-white/60 uppercase tracking-widest">Vehicles</div><div className="text-white font-bold text-base">2</div></div>
              <div className="bg-[#0E0E0E] rounded p-2"><div className="text-white/60 uppercase tracking-widest">Radios</div><div className="text-white font-bold text-base">Yes</div></div>
            </div>
            <div className="flex items-center gap-1 text-sm">
              {[1,2,3,4,5].map(i => <Star key={i} className={`w-4 h-4 ${i <= 4 ? "text-brand fill-brand" : "text-slate-200"}`} />)}
              <span className="text-white/60 ml-1 font-manrope">(18 team endorsements)</span>
            </div>
            <div className="lock-chip"><Lock className="w-3.5 h-3.5" /> Contact hidden until booking</div>
            <div className="text-xs text-white/60 font-manrope italic">Illustrative — real team profiles look exactly like this.</div>
          </div>
        </div>
      </div>

      {/* Use cases */}
      <section>
        <SectionHeader eyebrow="When to use a cluster team" title="Book teams when timing and capability matter more than headcount." />
        <div className="grid md:grid-cols-3 gap-4 mt-4">
          {USE_CASES.map((u) => (
            <div key={u.title} className="card-flat p-6 border-t-2 border-t-brand" data-testid={`cluster-usecase-${u.title.split(' ')[0].toLowerCase()}`}>
              <div className="w-10 h-10 rounded-md bg-brand-light text-brand flex items-center justify-center mb-3">
                <u.icon className="w-5 h-5" />
              </div>
              <div className="font-outfit font-bold text-white mb-2">{u.title}</div>
              <div className="text-sm text-white/70 font-manrope leading-relaxed">{u.body}</div>
            </div>
          ))}
        </div>
      </section>

      {/* 3 sides of the cluster */}
      <section>
        <SectionHeader eyebrow="How clusters work" title="Three ways to be part of a team." />
        <div className="grid md:grid-cols-3 gap-4 mt-4">
          {TEAM_ROLES.map((r) => (
            <div key={r.title} className="rounded-xl bg-navy-900 text-white p-6 space-y-3" data-testid={`cluster-role-${r.badge.toLowerCase().replace(/\W+/g, '-')}`}>
              <div className="inline-flex items-center gap-1 bg-white/10 border border-white/20 text-brand px-2 py-0.5 rounded-full text-[10px] font-outfit font-bold uppercase tracking-widest">
                {r.badge}
              </div>
              <div className="w-11 h-11 rounded-md bg-brand text-white flex items-center justify-center">
                <r.icon className="w-5 h-5" strokeWidth={2.2} />
              </div>
              <div className="font-outfit font-bold text-lg">{r.title}</div>
              <div className="text-sm text-slate-300 font-manrope leading-relaxed">{r.body}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Benefits grid */}
      <section>
        <SectionHeader eyebrow="Why cluster over individuals" title="Everything a company needs to see — before they even ask." />
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
          {TEAM_BENEFITS.map((b) => (
            <div key={b.title} className="card-flat p-5" data-testid={`cluster-benefit-${b.title.split(' ')[0].toLowerCase()}`}>
              <div className="w-10 h-10 rounded-md bg-brand-light text-brand flex items-center justify-center mb-3">
                <b.icon className="w-5 h-5" />
              </div>
              <div className="font-outfit font-semibold text-white mb-1">{b.title}</div>
              <div className="text-sm text-white/70 font-manrope leading-relaxed">{b.body}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Step-by-step */}
      <section className="grid lg:grid-cols-2 gap-6">
        <div className="card-flat p-6" data-testid="cluster-lead-steps">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-9 h-9 rounded-md bg-brand text-white flex items-center justify-center"><UserPlus className="w-4 h-4" /></div>
            <div className="text-xs uppercase tracking-widest text-brand font-outfit font-bold">If you are a Lead Pro</div>
          </div>
          <h3 className="text-xl font-outfit font-bold text-white mb-4">Set up your cluster in under 10 minutes.</h3>
          <ol className="space-y-4">
            {LEAD_STEPS.map((s) => (
              <li key={s.n} className="flex items-start gap-3">
                <div className="w-7 h-7 rounded-full bg-brand text-white flex items-center justify-center flex-shrink-0 font-outfit font-bold text-sm">{s.n}</div>
                <div>
                  <div className="font-outfit font-semibold text-white">{s.title}</div>
                  <div className="text-sm text-white/70 font-manrope">{s.body}</div>
                </div>
              </li>
            ))}
          </ol>
          <Button asChild className="mt-5 bg-brand hover:bg-brand-hover text-white w-full font-manrope font-semibold" data-testid="cluster-lead-cta">
            <Link to="/register/staff">Register as a professional — free <ChevronRight className="w-4 h-4 ml-1" /></Link>
          </Button>
        </div>

        <div className="card-flat p-6" data-testid="cluster-company-steps">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-9 h-9 rounded-md bg-navy-900 text-white flex items-center justify-center"><Building2 className="w-4 h-4" /></div>
            <div className="text-xs uppercase tracking-widest text-white font-outfit font-bold">If you are a Company</div>
          </div>
          <h3 className="text-xl font-outfit font-bold text-white mb-4">Book a full team in three taps.</h3>
          <ol className="space-y-4">
            {COMPANY_STEPS.map((s) => (
              <li key={s.n} className="flex items-start gap-3">
                <div className="w-7 h-7 rounded-full bg-navy-900 text-white flex items-center justify-center flex-shrink-0 font-outfit font-bold text-sm">{s.n}</div>
                <div>
                  <div className="font-outfit font-semibold text-white">{s.title}</div>
                  <div className="text-sm text-white/70 font-manrope">{s.body}</div>
                </div>
              </li>
            ))}
          </ol>
          <Button asChild className="mt-5 bg-navy-900 hover:bg-navy-800 text-white w-full font-manrope font-semibold" data-testid="cluster-company-cta">
            <Link to="/register/company">Register your company <ChevronRight className="w-4 h-4 ml-1" /></Link>
          </Button>
        </div>
      </section>

      {/* Fee example for team booking */}
      <section className="rounded-xl bg-navy-900 text-white p-8 grid md:grid-cols-2 gap-6" data-testid="cluster-fee-example">
        <div>
          <div className="text-xs uppercase tracking-widest text-brand font-outfit font-bold mb-2">Fee example — 5-pro team, 10hr event</div>
          <h3 className="text-2xl font-outfit font-bold leading-snug">Small teams, small transparent fee. No agency margin.</h3>
          <p className="text-slate-300 font-manrope mt-3 text-sm">
            The platform charges the <strong className="text-white">company</strong> either €1/hr per worker OR 3% of the booking — whichever is lower.
            Professionals keep 100% of their negotiated rate. No hidden costs, ever.
          </p>
          <div className="mt-4">
            <Button asChild variant="outline" className="border-white/30 text-white hover:bg-white/10">
              <Link to="/pricing" data-testid="cluster-pricing-link">See detailed pricing <ChevronRight className="w-4 h-4 ml-1" /></Link>
            </Button>
          </div>
        </div>
        <div className="bg-white/5 border border-white/10 rounded-lg p-5 font-manrope text-sm space-y-2">
          <div className="flex justify-between"><span className="text-slate-300">5 pros × €25/hr × 10 hr</span><strong>€1,250.00</strong></div>
          <div className="flex justify-between"><span className="text-slate-300">€1/hr × 5 workers × 10 hr</span><strong>€50.00</strong></div>
          <div className="flex justify-between"><span className="text-slate-300">3% of €1,250</span><strong>€37.50</strong></div>
          <div className="flex justify-between border-t border-white/10 pt-2 text-gold-400"><strong>Company pays platform (lower)</strong><strong>€37.50</strong></div>
          <div className="flex justify-between text-xs text-white/50"><span>Team take-home</span><span>€1,250.00 (100%)</span></div>
        </div>
      </section>

      {/* Big CTA */}
      <section className="text-center py-6">
        <div className="inline-flex items-center gap-2 bg-gold-50 text-gold-600 border border-gold-200 px-3 py-1 rounded-full text-xs font-outfit font-bold uppercase tracking-widest mb-4">
          <CalendarClock className="w-3.5 h-3.5" /> Ready-made teams available now
        </div>
        <h2 className="text-3xl sm:text-4xl font-outfit font-bold text-white mb-3">Move faster than a WhatsApp thread.</h2>
        <p className="text-white/70 font-manrope max-w-2xl mx-auto">
          Whether you lead operations or need to deploy one tomorrow, SecuSource clusters remove the coordination overhead.
        </p>
        <div className="mt-6 flex flex-col sm:flex-row justify-center gap-3">
          <Button asChild size="lg" className="bg-brand hover:bg-brand-hover text-white px-6 py-6 font-manrope font-semibold">
            <Link to="/register/staff" data-testid="cluster-final-lead">Join as a professional (free)</Link>
          </Button>
          <Button asChild size="lg" variant="outline" className="border-navy-900 text-white hover:bg-navy-900 hover:text-white px-6 py-6 font-manrope font-semibold">
            <Link to="/register/company" data-testid="cluster-final-book">Register as a company</Link>
          </Button>
        </div>
      </section>
    </div>
  );
}
